import { axiosInstanceN8NToken } from "../instance";

export enum CancelChatSender {
  USER = "user",
  BOT = "bot",
}

export interface CancelChatMessage {
  id: string;
  text: string;
  sender: CancelChatSender;
  timestamp: number;
}

export interface CancelChatRequest {
  message: string;
  session_id: string | null;
  env: "dev" | "prod";
  language: string;
}

export interface CancelChatResponse {
  output: {
    message: string;
    can_cancel: boolean;
  };
  session_id: string;
}

const CHAT_STORAGE_KEY = "fluxi_cancel_chat";
const CHAT_EXPIRATION_HOURS = 24;
const MILLISECONDS_PER_HOUR = 60 * 60 * 1000;

export interface StoredChatData {
  messages: CancelChatMessage[];
  sessionId: string | null;
  canCancel: boolean;
  createdAt: number;
}

export const mapperEnvironmentToEnv = (environment: string): "dev" | "prod" => {
  return environment === "production" ? "prod" : "dev";
};

class CancelChatService {

  async sendMessage(
    message: string,
    sessionId: string | null,
    language: string
  ): Promise<CancelChatResponse> {
    const env = mapperEnvironmentToEnv(import.meta.env.VITE_ENV || "development");
    
    const payload: CancelChatRequest = {
      message,
      session_id: sessionId,
      env,
      language,
    };

    const response = await axiosInstanceN8NToken.post<CancelChatResponse>(
      "/webhook/cancel-chat",
      payload,
    );

    return response.data;
  }

  saveChatState(data: {
    messages: CancelChatMessage[];
    sessionId: string | null;
    canCancel: boolean;
  }): void {    
    const storageData: StoredChatData = {
      ...data,
      createdAt: Date.now(),
    };

    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(storageData));
    } catch (error) {
      console.error("Error saving chat state:", error);
    }
  }

  loadChatState(): {
    messages: CancelChatMessage[];
    sessionId: string | null;
    canCancel: boolean;
  } | null {
    try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY);
      if (!stored) return null;

      const data: StoredChatData = JSON.parse(stored);
      const now = Date.now();
      const expirationTime = data.createdAt + CHAT_EXPIRATION_HOURS * MILLISECONDS_PER_HOUR;

      if (now > expirationTime) {
        this.clearChatState();
        return null;
      }

      return {
        messages: data.messages,
        sessionId: data.sessionId,
        canCancel: data.canCancel,
      };
    } catch (error) {
      console.error("Error loading chat state:", error);
      return null;
    }
  }

  clearChatState(): void {
    try {
      localStorage.removeItem(CHAT_STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing chat state:", error);
    }
  }

  isChatExpired(): boolean {
    try {
      const stored = localStorage.getItem(CHAT_STORAGE_KEY);
      if (!stored) return true;

      const data: StoredChatData = JSON.parse(stored);
      const now = Date.now();
      const expirationTime = data.createdAt + CHAT_EXPIRATION_HOURS * MILLISECONDS_PER_HOUR;

      return now > expirationTime;
    } catch (error) {
      return true;
    }
  }

  generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const cancelChatService = new CancelChatService();

